'use client';

import { Section as SparkySection } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';

import { trackContentfulClick } from '@libs/analytics';
import { clsx } from '@libs/common-next';

import { COLOR_TABLE } from '../../constants';
import { HeaderCtaTracker } from '../../utils/HeaderCtaTracker';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { propsImagesItems } from '../../utils/propsImagesItems';
import PromoCard from '../PromoCard';
import type { ProductCarouselProps } from './types';

export function Section({
    className,
    headline,
    description,
    linkAction,
    variant = 'default',
    card,
    isH1,
    linkActionColor = 'Blue',
    backgroundColor,
    textColor = 'Dark Grey',
    children,
    ...other
}: Omit<ProductCarouselProps, 'products'>) {
    const pathname = usePathname();

    const restyledVariants = ['default', 'storytelling', 'campaign'];
    const isRestyled = restyledVariants.includes(variant);

    const componentClassName = clsx(
        'product-carousel',
        `product-carousel--${variant}`,
        isRestyled && `product-carousel--restyled`,
        className
    );

    const { link, actionText } = linkAction || {};
    const cardDescription = card?.bodyText ?? card?.description;
    const displayHeadline = getTrimmedDisplayTitle(headline);
    const { title: sectionTitle, headingTagName } = getPlpModuleSectionHeading(headline, isH1);
    const cardHeaderTagName: 'h2' | 'h3' = isH1 || !displayHeadline ? 'h2' : 'h3';

    return (
        <SparkySection
            data-testid="product-carousel"
            title={sectionTitle}
            description={description}
            headingTagName={headingTagName}
            {...(isRestyled && {
                align: 'center',
                titleAfterLocation: 'below-description',
            })}
            titleAfter={
                link?.webUrl && actionText ? (
                    <HeaderCtaTracker
                        textLink={linkAction}
                        linkTextColor={linkActionColor}
                        header={displayHeadline ?? ''}
                        promoVariant={`product-carousel - ${variant}`}
                        promoType={`product-carousel--${variant}`}
                    />
                ) : null
            }
            className={componentClassName}
            {...(variant === 'campaign' && {
                style: {
                    backgroundColor,
                    color: COLOR_TABLE[textColor],
                },
            })}
            {...other}
        >
            {variant === 'storytelling' && card && (
                <div className="product-carousel__card">
                    <PromoCard
                        data-testid="product-carousel-card"
                        header={card.header}
                        headerTagName={cardHeaderTagName}
                        description={cardDescription}
                        imageItems={propsImagesItems(card.image)}
                        imgAlt={card.image.webAltText}
                        link={card.link}
                        onClick={() => {
                            trackContentfulClick({
                                linkText: `${card.header} ${cardDescription}`,
                                linkUrl: card.link?.webUrl ?? '',
                                promoTitle: headline ?? '',
                                promoVariant: 'product carousel - storytelling',
                                promoDesc: 'product-carousel__card',
                                promoType: 'product carousel - storytelling',
                                promoOrigin: pathname,
                            });
                        }}
                    />
                </div>
            )}

            {children}
        </SparkySection>
    );
}
